// Exports a coach's marketing page as a single, self-contained static HTML
// file they can host anywhere (their own domain, Squarespace, etc.) — sibling
// to pdfExport.js's "assemble a document client-side" pattern, but producing
// an HTML string instead of a jsPDF document. Colors are literal hex here
// (copied from the app's --forest/--sage/--mint/--gold/--cream design tokens)
// since a downloaded file can't depend on the app's global stylesheet.

const escapeHtml = (str) => String(str || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export function exportMarketingPageHTML(page, profile) {
  if (!page) return;

  const coachName = profile?.practice_name || profile?.full_name || '';
  const heroPhoto = (page.photos || []).find(p => p.placement === 'hero');
  const galleryPhotos = (page.photos || []).filter(p => p.placement !== 'hero');
  const outcomes = page.outcomes || [];

  const heroStyle = heroPhoto
    ? `background-image: linear-gradient(rgba(26,46,40,0.72), rgba(26,46,40,0.72)), url('${escapeHtml(heroPhoto.url)}'); background-size: cover; background-position: center;`
    : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(page.headline) || 'My Coaching Practice'}</title>
<!-- Drafted with AI assistance, personalized by ${escapeHtml(coachName) || 'the coach'}. -->
<style>
  :root {
    --forest: #1A2E28; --sage: #5C7A6B; --mint: #EBF0ED; --gold: #C4956A;
    --gold-soft: #E8CBA9; --cream: #F7F3EE; --text-dark: #1C2B26;
  }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: var(--text-dark); background: var(--cream); }
  .hero { padding: 80px 24px; text-align: center; background: var(--forest); color: white; ${heroStyle} }
  .hero-inner { max-width: 720px; margin: 0 auto; }
  .eyebrow { font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--gold-soft); margin-bottom: 14px; }
  h1 { font-family: Georgia, 'Times New Roman', serif; font-size: clamp(28px, 5vw, 46px); font-weight: 400; line-height: 1.2; margin: 0 0 16px; }
  .hook { font-size: 17px; line-height: 1.6; color: rgba(255,255,255,0.85); margin: 0; }
  section { padding: 56px 24px; }
  .section-inner { max-width: 720px; margin: 0 auto; }
  .section-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--sage); margin-bottom: 14px; }
  .body-text { font-size: 17px; line-height: 1.75; margin: 0; }
  .body-text-large { font-family: Georgia, serif; font-size: 22px; line-height: 1.6; margin: 0; font-style: italic; }
  .transformation { background: var(--mint); }
  .gallery { display: flex; gap: 4px; overflow-x: auto; }
  .gallery img { width: 260px; height: 200px; object-fit: cover; flex-shrink: 0; }
  .outcomes { background: var(--forest); }
  .outcomes .section-label { color: var(--gold-soft); }
  .outcomes-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }
  .quote-block { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; padding: 20px; }
  .quote-mark { font-family: Georgia, serif; font-size: 32px; color: var(--gold); line-height: 1; margin-bottom: 4px; }
  .quote-text { font-size: 15px; line-height: 1.6; color: rgba(255,255,255,0.9); font-style: italic; margin: 0; }
  .cta { padding: 64px 24px; text-align: center; background: var(--cream); }
  .cta-btn { display: inline-block; padding: 16px 36px; border-radius: 10px; background: var(--sage); color: white; text-decoration: none; font-size: 16px; font-weight: 600; }
</style>
</head>
<body>
  <div class="hero">
    <div class="hero-inner">
      ${coachName ? `<div class="eyebrow">${escapeHtml(coachName)}</div>` : ''}
      <h1>${escapeHtml(page.headline) || 'Your headline goes here'}</h1>
      ${page.hook_subtext ? `<p class="hook">${escapeHtml(page.hook_subtext)}</p>` : ''}
    </div>
  </div>

  ${page.pain_point ? `<section><div class="section-inner"><p class="body-text">${escapeHtml(page.pain_point)}</p></div></section>` : ''}

  ${page.transformation ? `<section class="transformation"><div class="section-inner"><div class="section-label">What becomes possible</div><p class="body-text-large">${escapeHtml(page.transformation)}</p></div></section>` : ''}

  ${page.approach ? `<section><div class="section-inner"><div class="section-label">How we'll work together</div><p class="body-text">${escapeHtml(page.approach)}</p></div></section>` : ''}

  ${galleryPhotos.length > 0 ? `<div class="gallery">${galleryPhotos.map(p => `<img src="${escapeHtml(p.url)}" alt="${escapeHtml(p.caption)}">`).join('')}</div>` : ''}

  ${outcomes.length > 0 ? `<section class="outcomes"><div class="section-inner"><div class="section-label">Real results</div><div class="outcomes-grid">${outcomes.map(o => `<div class="quote-block"><div class="quote-mark">"</div><p class="quote-text">${escapeHtml(o.text)}</p></div>`).join('')}</div></div></section>` : ''}

  <section class="cta">
    <div class="section-inner">
      <a href="${escapeHtml(page.cta_url) || '#'}" class="cta-btn">${escapeHtml(page.cta_text) || 'Get started'}</a>
    </div>
  </section>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const filename = `landing_page_${(coachName || 'my_practice').replace(/\s+/g, '_').toLowerCase()}.html`;
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
